// Web Bluetooth ESC/POS thermal printer helper
// Works with 58mm/80mm thermal printers that expose a Serial Port Service (0000ff00-0000-1000-8000-00805f9b34fb)

const PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
];

let printerDevice = null;
let printerCharacteristic = null;

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

function encText(s) {
  // CP437 default; fallback to latin-1
  const enc = new TextEncoder();
  return enc.encode(s);
}

function cmd(...bytes) { return new Uint8Array(bytes); }

function concat(arrs) {
  let len = 0;
  arrs.forEach((a) => (len += a.length));
  const out = new Uint8Array(len);
  let off = 0;
  arrs.forEach((a) => { out.set(a, off); off += a.length; });
  return out;
}

export function isBluetoothSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

export async function connectPrinter() {
  if (!isBluetoothSupported()) {
    throw new Error("Browser Anda tidak mendukung Web Bluetooth. Gunakan Chrome/Edge di desktop atau Android.");
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });
  const server = await device.gatt.connect();
  // try services
  let service = null;
  for (const uuid of PRINTER_SERVICES) {
    try { service = await server.getPrimaryService(uuid); if (service) break; } catch (e) {}
  }
  if (!service) {
    const services = await server.getPrimaryServices();
    service = services[0];
  }
  if (!service) throw new Error("Tidak ada service printer yang ditemukan.");
  const chars = await service.getCharacteristics();
  const writable = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse);
  if (!writable) throw new Error("Tidak ada karakteristik yang dapat ditulis.");
  printerDevice = device;
  printerCharacteristic = writable;
  device.addEventListener("gattserverdisconnected", () => {
    printerDevice = null;
    printerCharacteristic = null;
  });
  return device.name || "Printer";
}

export function isPrinterConnected() {
  return !!(printerDevice && printerDevice.gatt && printerDevice.gatt.connected);
}

async function writeChunks(data) {
  if (!printerCharacteristic) throw new Error("Printer belum terhubung.");
  const chunkSize = 180;
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    if (printerCharacteristic.writeWithoutResponse) {
      await printerCharacteristic.writeWithoutResponse(chunk);
    } else {
      await printerCharacteristic.writeValue(chunk);
    }
    await new Promise((r) => setTimeout(r, 20));
  }
}

function padLine(left, right, width = 32) {
  const l = String(left);
  const r = String(right);
  const space = Math.max(1, width - l.length - r.length);
  return l + " ".repeat(space) + r;
}

function formatRp(n) {
  return "Rp" + Math.round(n).toLocaleString("id-ID");
}

export async function printReceipt(order, settings) {
  if (!printerCharacteristic) {
    throw new Error("Printer belum terhubung. Klik 'Hubungkan Printer' terlebih dahulu.");
  }
  const parts = [];
  // init
  parts.push(cmd(ESC, 0x40)); // init
  parts.push(cmd(ESC, 0x61, 0x01)); // center
  parts.push(cmd(ESC, 0x21, 0x30)); // double size
  parts.push(encText((settings?.restaurant_name || "Warung Kasir") + "\n"));
  parts.push(cmd(ESC, 0x21, 0x00)); // normal
  parts.push(encText("Struk Pembayaran\n"));
  parts.push(encText("--------------------------------\n"));
  parts.push(cmd(ESC, 0x61, 0x00)); // left
  parts.push(encText(padLine("No", order.order_no) + "\n"));
  parts.push(encText(padLine("Meja", order.table_number || "-") + "\n"));
  if (order.customer_name) parts.push(encText(padLine("Nama", order.customer_name) + "\n"));
  parts.push(encText(padLine("Waktu", new Date(order.created_at).toLocaleString("id-ID")) + "\n"));
  parts.push(encText("--------------------------------\n"));

  for (const it of order.items || []) {
    const line1 = `${it.quantity}x ${it.name}`;
    const optSum = (it.options || []).reduce((a, o) => a + (o.price_delta || 0), 0);
    const subtotal = (it.unit_price + optSum) * it.quantity;
    parts.push(encText(padLine(line1.substring(0, 24), formatRp(subtotal)) + "\n"));
    for (const o of it.options || []) {
      parts.push(encText("  + " + o.name + "\n"));
    }
    if (it.notes) parts.push(encText("  Catatan: " + it.notes + "\n"));
  }
  parts.push(encText("--------------------------------\n"));
  if (order.discount && order.discount > 0) {
    const sub = order.subtotal || (order.total + order.discount);
    parts.push(encText(padLine("Subtotal", formatRp(sub)) + "\n"));
    parts.push(encText(padLine(`Diskon ${order.promo?.code || ""}`.trim(), "-" + formatRp(order.discount)) + "\n"));
  }
  parts.push(cmd(ESC, 0x21, 0x10)); // double height
  parts.push(encText(padLine("TOTAL", formatRp(order.total)) + "\n"));
  parts.push(cmd(ESC, 0x21, 0x00));
  parts.push(encText("--------------------------------\n"));
  parts.push(cmd(ESC, 0x61, 0x01));
  parts.push(encText("Terima kasih!\n"));
  parts.push(encText("\n\n\n"));
  parts.push(cmd(GS, 0x56, 0x00)); // cut

  await writeChunks(concat(parts));
}
